import React, { useEffect, useRef, useState } from 'react';
import PageContainer from '../../../components/container/PageContainer';
import {
  PROJECT_HEADER,
  PROJECT_MODAL_FIELDS,
} from '../../../constants/project';
import {
  EmptyProjectsGetResponse,
  ProjectsArray,
  ProjectsGetResponse,
} from '../../../types/project/ProjectGetResponse';
import {
  createProject,
  deleteProject,
  getProjectList,
  updateProject,
} from '../../../services/api';
import { Col, Row } from 'react-bootstrap';
import RenderProjectRow from './RenderProjectRow';
import EditProjectRow from './EditProjectRow';
import AddProjectRow from './AddProjectRow';
import SingleAddTable from '../../../components/table/SingleAddTable';
import {
  EmptyProjectPostRequest,
  ProjectPostRequest,
} from '../../../types/project/ProjectPostRequest';
import {
  EmptyProjectPutRequest,
  ProjectPutRequest,
} from '../../../types/project/ProjectPutRequest';
import TablePagination from '../../../components/table/pagination/TablePagination';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import LoadingOverlay from '../../../components/LoadingOverlay';
import {
  DefaultProjectGetQueryParams,
  ProjectGetQueryParams,
} from '../../../types/project/ProjectGetQueryParams';
import { SortOrder } from '../../../types/sort/sort';
import {
  DEFAULT_SORT_ORDER,
  PROJECT_DEFAULT_SORT_COLUMN,
} from '../../../constants/sort';
import useValidation from '../../../hooks/useValidation';
import { PROJECT_VALIDATION } from '../../../constants/validation/projectValidation';
import ProjectSearch from '../../../components/search/ProjectSearch';
import UpdateModalSave from '../../../components/modal/UpdateModalSave';
import DeleteModal from '../../../components/modal/DeleteModal';
import { EmptyProjectDeleteItem } from '../../../types/project/ProjectDeleteItem';
import AddButton from '../../../components/button/AddButton';
import SearchResetButtons from '../../../components/button/SearchResetButtons';
import TokyoCheckbox from '../../../components/search/TokyoCheckbox';
import DairenCheckbox from '../../../components/search/DairenCheckbox';
import { isFirstLetterD } from '../../../utils/isFirstLetter';
import { VALIDATION_TEMPLATE } from '../../../constants/validation/validationTemplete';
import SyncedScrollHeader from '../../../components/layout/SyncedScrollHeader';
import '../../../styles/ProjectHeader.css';

const Project = () => {
  const [editingId, setEditingId] = useState<number>(-1);
  const [isAdding, setIsAdding] = useState(false);
  const [addingItem, setAddingItem] = useState<ProjectPostRequest>(
    EmptyProjectPostRequest
  );
  const [editingItem, setEditingItem] = useState<ProjectPutRequest>(
    EmptyProjectPutRequest
  );
  const [deletingItem, setDeletingItem] = useState<ProjectsArray>(
    EmptyProjectDeleteItem
  );
  const [getQueryParams, setGetQueryParams] = useState<ProjectGetQueryParams>(
    DefaultProjectGetQueryParams
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(DEFAULT_SORT_ORDER);
  const [activeSortColumn, setActiveSortColumn] = useState<string>(
    PROJECT_DEFAULT_SORT_COLUMN
  );
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resetSignal, setResetSignal] = useState(0); // 検索リセット用のシグナル

  // 表の横スクロール要素へのref（ヘッダー同期用）※構造的Ref
  const tableScrollRef = useRef<HTMLDivElement>(null);
  // ヘッダーのref（高さ計算用）
  const headerRef = useRef<HTMLDivElement>(null);
  // フッターのref（高さ計算用）
  const footerRef = useRef<HTMLDivElement>(null);

  // バリデーション関数。エラー時はエラーメッセージを返す。
  const { validate } = useValidation(PROJECT_VALIDATION);

  const queryClient = useQueryClient();

  // プロジェクト検索のクエリパラメーターをセット
  const setSearchQueryParams = (id: number) => {
    setGetQueryParams((prev) => ({
      ...prev,
      projectId: id,
    }));
  };

  // post, put delete成功時の処理
  const handleSubmitSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['projectList'],
    });
    setIsAdding(false);
    setAddingItem(EmptyProjectPostRequest);
    setEditingId(-1);
  };

  // 一覧取得
  const {
    data: projectGetResponse,
    isLoading,
    isError,
    error,
  } = useQuery<ProjectsGetResponse>({
    queryKey: ['projectList'],
    queryFn: () => getProjectList(getQueryParams),
    initialData: EmptyProjectsGetResponse,
  });

  // ページ数、ソート順、ソートカラムが変わったら一覧を再取得
  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['projectList'],
    });
  }, [getQueryParams.page, getQueryParams.sortKey, getQueryParams.sortOrder]);

  // 一覧取得失敗時のアラート
  useEffect(() => {
    if (isError) {
      alert(error as any);
    }
  }, [isError]);

  // 追加
  const postRequest = useMutation({
    mutationFn: (data: ProjectPostRequest) => createProject(data),
    onSuccess: handleSubmitSuccess,
    onError: (error: any) => alert(error.response.data.message),
  });

  // 削除
  const deleteRequest = useMutation({
    mutationFn: (id: number) => deleteProject(id),
    onSuccess: handleSubmitSuccess,
    onError: (error: any) => alert(error.response.data.message),
  });

  // 編集
  const editRequest = useMutation({
    mutationFn: (data: ProjectPutRequest) => updateProject(data.id, data),
    onSuccess: handleSubmitSuccess,
    onError: (error: any) => alert(error.response.data.message),
  });

  const onAddButtonClick = () => {
    setIsAdding(true);
    // 編集状態をリセット
    setEditingId(-1);
    setAddingItem(EmptyProjectPostRequest);
  };

  const onEditButtonClick = (item: ProjectsArray) => {
    // 新規作成状態をリセット
    setIsAdding(false);
    setAddingItem(EmptyProjectPostRequest);
    setEditingId(item.id);
    setEditingItem({
      ...item,
    });
  };

  const onDeleteButtonClick = (item: ProjectsArray) => {
    setDeletingItem(item);
    setShowDeleteModal(true);
  };

  const onEditCancelButtonClick = () => {
    setEditingId(-1);
  };

  const onAddSubmitButtonClick = async () => {
    const validationErrors = validate(addingItem);
    if (validationErrors) {
      alert(validationErrors);
      return;
    }

    if (addingItem.isDairen && !isFirstLetterD(addingItem.projectNumber)) {
      alert(VALIDATION_TEMPLATE.dairen('プロジェクトナンバー'));
      return;
    }

    postRequest.mutate(addingItem);
  };

  const onEditSubmitButtonClick = async () => {
    setShowUpdateModal(true);
  };

  const onEditModalSubmitButtonClick = async () => {
    const validationErrors = validate(editingItem);
    if (validationErrors) {
      alert(validationErrors);
      return;
    }

    if (addingItem.isDairen && !isFirstLetterD(addingItem.projectNumber)) {
      alert(VALIDATION_TEMPLATE.dairen('プロジェクトナンバー'));
      return;
    }

    editRequest.mutate({ ...editingItem });
  };

  const onSubmitCancelButtonClick = () => {
    setIsAdding(false);
    setAddingItem(EmptyProjectPostRequest);
  };

  const handleAddItemChange = (
    key: keyof ProjectPostRequest,
    value: string | boolean
  ) => {
    setAddingItem((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleEditItemChange = (
    key: keyof ProjectPutRequest,
    value: string | boolean
  ) => {
    setEditingItem((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // ソートの状態を管理
  const handleSort = (sortKey: string) => {
    if (sortKey === activeSortColumn) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setActiveSortColumn(sortKey);
      setSortOrder('desc');
    }
  };

  // ソートの状態が変わったときにクエリパラメータを更新
  useEffect(() => {
    setGetQueryParams((prev) => ({
      ...prev,
      sortOrder: sortOrder,
      sortKey: activeSortColumn,
    }));
  }, [sortOrder, activeSortColumn]);

  // post, put, deleteが実行中かどうかをチェック
  const isMutating = [postRequest, deleteRequest, editRequest].some(
    (m) => m.status === 'pending'
  );

  return (
    <PageContainer title="プロジェクト一覧">
      <LoadingOverlay isLoading={isLoading} isMutating={isMutating} />
      <SyncedScrollHeader
        targetRef={tableScrollRef}
        className="mb-3"
        outerRef={headerRef}
      >
        <Row className="header-row-project gx-0">
          {/* 左：検索条件（幅は固定で縮めない） */}
          <Col className="header-fields-project order-1">
            <ProjectSearch
              setSelectedId={setSearchQueryParams}
              resetSignal={resetSignal}
            />
            <TokyoCheckbox
              className="ms-3"
              checked={getQueryParams.isTokyo}
              onChange={(checked) =>
                setGetQueryParams((prev) => ({ ...prev, isTokyo: checked }))
              }
            />
            <DairenCheckbox
              className="ms-3"
              checked={getQueryParams.isDairen}
              onChange={(checked) =>
                setGetQueryParams((prev) => ({ ...prev, isDairen: checked }))
              }
            />
          </Col>

          {/* 右：ボタン群（検索/リセットは常に横並び、追加は折り返し対象） */}
          <Col xs="auto" className="header-right-project order-2">
            <div className="actions-group-project">
              <div className="actions-searchreset-project">
                <SearchResetButtons
                  onSearch={() => {
                    // 検索ボタン押下時はページを1に戻す
                    setGetQueryParams((prev) => ({ ...prev, page: 1 }));
                    const paramsForSearch = {
                      ...getQueryParams,
                      page: 1,
                    };
                    queryClient.fetchQuery({
                      queryKey: ['projectList'],
                      queryFn: () => getProjectList(paramsForSearch),
                    });
                  }}
                  onReset={() => {
                    setGetQueryParams(DefaultProjectGetQueryParams);
                    queryClient.fetchQuery({
                      queryKey: ['projectList'],
                      queryFn: () =>
                        getProjectList(DefaultProjectGetQueryParams),
                    });
                    setResetSignal((p) => p + 1);
                  }}
                />
              </div>
              <div className="actions-extra-project">
                <AddButton
                  onClick={onAddButtonClick}
                  buttonLabel="プロジェクト"
                />
              </div>
            </div>
          </Col>
        </Row>
      </SyncedScrollHeader>

      <SingleAddTable<ProjectsArray>
        headers={PROJECT_HEADER}
        items={projectGetResponse.projects}
        actionable
        scrollContainerRef={tableScrollRef}
        headerRef={headerRef}
        footerRef={footerRef}
        renderItem={(item) => (
          <RenderProjectRow
            item={item}
            editingId={editingId}
            onDeleteButtonClick={onDeleteButtonClick}
            onEditButtonClick={(i) => onEditButtonClick(i)}
          />
        )}
        editItem={() => (
          <EditProjectRow
            item={editingItem}
            onChange={handleEditItemChange}
            onEditSubmitButtonClick={onEditSubmitButtonClick}
            onEditCancelButtonClick={onEditCancelButtonClick}
          />
        )}
        addItem={() => (
          <AddProjectRow
            item={addingItem}
            onChange={handleAddItemChange}
            onAddSubmitButtonClick={onAddSubmitButtonClick}
            onSubmitCancelButtonClick={onSubmitCancelButtonClick}
          />
        )}
        isAdding={isAdding}
        setIsAdding={setIsAdding}
        editingId={editingId}
        onSort={handleSort}
        sortOrder={sortOrder}
        activeSortColumn={activeSortColumn}
      />

      <TablePagination
        totalPages={projectGetResponse.totalPages}
        onPageChange={(page) =>
          setGetQueryParams((prev) => ({ ...prev, page }))
        }
        ref={footerRef}
      />

      {showUpdateModal && (
        <UpdateModalSave<ProjectPutRequest>
          show={showUpdateModal}
          item={editingItem}
          fields={PROJECT_MODAL_FIELDS}
          onClose={() => setShowUpdateModal(false)}
          onSave={() => {
            onEditModalSubmitButtonClick();
            setShowUpdateModal(false);
          }}
        />
      )}
      {showDeleteModal && (
        <DeleteModal<ProjectsArray>
          show={showDeleteModal}
          item={deletingItem}
          fields={PROJECT_MODAL_FIELDS}
          onClose={() => setShowDeleteModal(false)}
          onDelete={() => {
            deleteRequest.mutate(deletingItem.id);
            setShowDeleteModal(false);
          }}
        />
      )}
    </PageContainer>
  );
};

export default Project;
